// src/pages/More.js

import React, { useState, useEffect, useRef, useCallback } from "react";
import version from "../version";
import AboutModal from "../components/AboutModal";
import { useApi } from "../apiClient";
import { useClerk } from "@clerk/clerk-react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import FeedbackModal from "../components/FeedbackModal";
import AdminDashboardModal from "../components/AdminDashboardModal";
import { useLang } from "../contexts/LanguageContext";
import locales from "../utils/locales";
import { motion, AnimatePresence } from "framer-motion";
import { Languages, ChartSpline, Star, Download, Infinity } from "lucide-react";
import CustomReminderForm from "../components/CustomReminderForm";
import { getNextReminderTime } from "../utils/reminderUtils";
import CSVModal from "./CSVModal";

// === Special Setting ===
function SpecialSettingCard({ user, userProfile }) {
  const api = useApi();
  const [open, setOpen] = useState(false);
  const [currentAge, setCurrentAge] = useState("");
  const [targetAge, setTargetAge] = useState("");
  const [saving, setSaving] = useState(false);
  const { lang } = useLang();
  const t = locales[lang];

  // preload age จาก userProfile
  useEffect(() => {
    if (userProfile?.current_age) setCurrentAge(String(userProfile.current_age));
    if (userProfile?.target_age) setTargetAge(String(userProfile.target_age));
  }, [userProfile]);

  // คำนวนปีเกิด
  const birthYear =
    currentAge && !isNaN(Number(currentAge))
      ? new Date().getFullYear() - Number(currentAge)
      : "";

  // เช็ค Target Age ถูกต้อง
  const maxHumanAge = 122;
  const minTargetAge = Number(currentAge) + 1;
  const isTargetValid =
    targetAge &&
    !isNaN(Number(targetAge)) &&
    Number(targetAge) > Number(currentAge) &&
    Number(targetAge) <= maxHumanAge;

  // ชั่วโมงที่เหลือ (ใช้ birth year เพื่อให้ target date คงที่ ไม่ขึ้นกับ "วันนี้")
  const hoursLeft = (() => {
    if (!isTargetValid || !birthYear) return null;
    const targetDate = new Date(Number(birthYear) + Number(targetAge), 6, 1); // 1 ก.ค. ของปีเป้าหมาย
    const diffMs = targetDate - new Date();
    return diffMs > 0 ? diffMs / (1000 * 60 * 60) : 0;
  })();

  async function handleSave() {
    if (!isTargetValid || saving) return;
    setSaving(true);
    try {
      await api.profile.upsert({
        current_age: Number(currentAge),
        target_age: Number(targetAge),
      });
      toast.success(t.save || "Saved!");
    } catch {
      toast.error(t.save_failed || "Save failed!");
    }
    setSaving(false);
  }

  return (
    <div className="my-3">
      <button
        className="w-full flex items-center gap-2 bg-purple-50 border border-purple-200 rounded-xl px-4 py-2 font-semibold shadow hover:bg-purple-100 transition"
        onClick={() => setOpen((o) => !o)}
      >
        <span role="img" aria-label="magic">
          🪄
        </span>
        {t.special_setting}
        <span className="ml-auto">{open ? "▲" : "▼"}</span>
      </button>
      {open && (
        <div className="p-4 mt-2 bg-white border rounded-2xl shadow-inner space-y-4 transition-all">
          <div>
            <label className="block text-sm font-semibold mb-1">
              {t.current_age}
            </label>
            <input
              type="number"
              className="border rounded px-3 py-2 w-full"
              value={currentAge}
              min={0}
              max={maxHumanAge - 1}
              onChange={e => {
                setCurrentAge(e.target.value.replace(/^0+/, ""));
                setTargetAge(""); // reset target age
              }}
              placeholder={t.current_age_placeholder}
              disabled={saving}
            />
            {birthYear && (
              <div className="text-xs text-gray-500 mt-1">
                {t.birth_year.replace("{year}", birthYear)}
              </div>
            )}
          </div>
          {currentAge && (
            <div>
              <label className="block text-sm font-semibold mb-1">
                {t.target_age}
              </label>
              <input
                type="number"
                className="border rounded px-3 py-2 w-full"
                value={targetAge}
                min={minTargetAge}
                max={maxHumanAge}
                onChange={e => setTargetAge(e.target.value.replace(/^0+/, ""))}
                placeholder={t.target_age_placeholder.replace("{max}", maxHumanAge)}
                disabled={saving}
              />
              {!isTargetValid && targetAge && (
                <div className="text-xs text-red-500 mt-1">
                  {t.target_age_invalid.replace("{max}", maxHumanAge)}
                </div>
              )}
              <button
                className="mt-3 w-full bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-xl px-4 py-2 shadow disabled:bg-gray-300"
                onClick={handleSave}
                disabled={!isTargetValid || saving}
              >
                {saving ? (t.saving || "Saving...") : (t.save || "Save")}
              </button>
            </div>
          )}
          {isTargetValid && (
            <div className="text-lg text-purple-700 mt-4 text-center font-bold">
              {t.hours_left
                .replace("{hours}", hoursLeft.toLocaleString())
                .replace("{cur}", currentAge)
                .replace("{target}", targetAge)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// === Helper: ฟอร์แมตเวลาให้รองรับได้ทุก type ===
function formatDateTime(dt, lang = "th") {
  if (!dt) return "";
  let d;
  if (typeof dt === "string") {
    if (dt.length === 5 && dt.match(/^\d{2}:\d{2}$/)) {
      // เวลา HH:mm (ใช้วันนี้)
      const now = new Date();
      const [hh, mm] = dt.split(":");
      now.setHours(Number(hh), Number(mm), 0, 0);
      d = now;
    } else {
      d = new Date(dt);
    }
  } else {
    d = dt;
  }
  if (isNaN(d)) return dt;
  return d.toLocaleString(lang === "th" ? "th-TH" : "en-US", {
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit"
  });
}

// === Missed Reminder Modal ===
function MissedReminderModal({ reminders, lang, onDismiss, onClose }) {
  if (!reminders.length) return null;
  return (
    <div className="fixed z-[9999] inset-0 bg-black/40 flex items-center justify-center">
      <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-md flex flex-col">
        <div className="font-bold text-lg mb-2">
          {lang === "th" ? "Reminder ที่เลยเวลา" : "Missed Reminders"}
        </div>
        <ul className="space-y-3 max-h-64 overflow-y-auto">
          {reminders.map((r, idx) => (
            <li key={idx} className="flex flex-col gap-1 border-b last:border-b-0 pb-2">
              <span className="text-lg">🔔 {r.message}</span>
              <span className="text-gray-400 text-sm">
                {formatDateTime(r.time, lang)}
              </span>
              <button className="mt-1 px-3 py-1 bg-gray-200 rounded hover:bg-gray-300 text-xs self-start"
                onClick={() => onDismiss(idx)}>
                {lang === "th" ? "ลบจากรายการ" : "Dismiss"}
              </button>
            </li>
          ))}
        </ul>
        <button className="mt-5 px-4 py-2 rounded bg-blue-500 text-white font-semibold shadow hover:bg-blue-600"
          onClick={onClose}>{lang === "th" ? "ปิด" : "Close"}</button>
      </div>
    </div>
  );
}

// === Main Component ===

const APP_URL = "https://spendora.thatwhy.me";
const adminEmails = ["thungong.c@gmail.com"];

export default function More({ userProfile, user }) {
  const api = useApi();
  const { signOut } = useClerk();
  // ---- Core State ----
  const [showAbout, setShowAbout] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [showAdmin, setShowAdmin] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [chartView, setChartView] = useState(() => localStorage.getItem("chartView") || "weekly");
  const [userRole, setUserRole] = useState("loading");
  const [checkingUpdate, setCheckingUpdate] = useState(false);
  const [showUpdated, setShowUpdated] = useState(false);
  const [activities, setActivities] = useState([]);
  const [features, setFeatures] = useState({});
  const [showReminderForm, setShowReminderForm] = useState(false);
  const [motivationQuote, setMotivationQuote] = useState(null);

  const handleShowMotivation = () => {
    const quotes = lang === "th"
      ? [
          "อย่ารอให้พร้อม เพราะบางครั้ง 'เริ่มเลย' คือคำตอบที่ใช่ที่สุด",
          "ถ้าอยากไปให้ไกล ต้องเริ่มก้าวแรกให้ได้ก่อน",
          "สิ่งเล็ก ๆ ที่ทำซ้ำ ๆ จะกลายเป็นพลังยิ่งใหญ่",
          "ใช้วันนี้ให้เต็มที่ เพราะพรุ่งนี้อาจไม่มีคำว่า 'เดี๋ยว'",
          "สิ่งสำคัญไม่ได้อยู่ที่ว่าทำเร็วหรือช้า แต่อยู่ที่ 'ไม่หยุดเดิน'",
          "ความสำเร็จเล็ก ๆ ทุกวัน คือก้อนหิมะที่กลิ้งใหญ่ขึ้นทุกเช้า",
          "อย่ากลัววันธรรมดา เพราะวันธรรมดา คือวันที่เราสร้างอนาคต",
          "ความพยายามวันนี้ คือของขวัญให้ตัวเองในวันข้างหน้า",
          "แม้จะช้า ขอแค่อย่า 'หยุดเดิน'",
          "การเปลี่ยนแปลงจะเริ่มต้นได้ ก็ต่อเมื่อเรารู้ตัวว่าใช้เวลากับอะไร",
          "นิสัยเล็ก ๆ สร้างการเปลี่ยนแปลงใหญ่",
          "เริ่มจากสิ่งเล็ก ๆ แล้วทำให้สม่ำเสมอ ดีกว่าทำครั้งละมาก ๆ แล้วหยุดไป",
          "เวลา คือการลงทุนที่แท้จริง",
          "จัดเวลาให้กับสิ่งที่เยียวยาใจคุณ เช่น ออกกำลังกาย หรือหัวเราะกับคนที่รัก",
          "อย่าแข่งขันกับใคร ให้เมตตากับตัวเอง",
          "ชีวิตคือการเดินทางเทียบกับ ‘ตัวคุณเมื่อวานนี้’ ต่างหาก",
          "การล้มไม่ใช่ความพ่ายแพ้ สิ่งสำคัญคือการลุกขึ้นอีกครั้ง…ด้วยความเข้าใจ",
          "การทบทวน คือเส้นทางสู่การเติบโต",
          "ทุกสัปดาห์ ลองเขียน 2 สิ่ง: สิ่งที่ภูมิใจ และสิ่งที่อยากปรับปรุง",
          "ใช้เวลาให้มีคุณค่า…เหมือนที่คุณใส่ใจกับเงินทุกบาท",
          "ไม่ต้องรอให้สมบูรณ์แบบ แค่เริ่มแล้วค่อย ๆ พัฒนา"
        ]
      : [
          "Don't wait to be ready. Sometimes 'Start now' is the best plan.",
          "If you want to go far, take the first step today.",
          "Small daily efforts lead to big results.",
          "Live fully today—there might not be a 'later'.",
          "Progress is better than perfection—just move forward.",
          "Every tiny win builds your momentum.",
          "Ordinary days make extraordinary futures.",
          "Today's effort is tomorrow's gift to yourself.",
          "Even slow progress is progress. Just don’t stop.",
          "Change begins with awareness.",
          "Write one line a day: What did you spend time on? And how did it make you feel?",
          "Small habits, big change.",
          "Start small. Be consistent. That’s more powerful than being intense.",
          "Time is the real investment.",
          "Block time for what restores you—exercise, hobbies, laughter with loved ones.",
          "Grace for yourself, not competition.",
          "You don’t have to keep up with anyone. The real journey is with your past self.",
          "No shame in falling. Just rise gently.",
          "Reflection brings growth.",
          "Growth isn’t just doing more. It’s seeing clearly.",
          "Each Sunday, write two things: What you’re proud of, and what to improve.",
          "Spend time like you spend money.",
          "Learn to guard your focus and spend your hours like treasures.",
          "You don't have to be great to start—just start and grow."
        ];
    const random = quotes[Math.floor(Math.random() * quotes.length)];
    setMotivationQuote(random);
  };

  // ---- Reminder State ----
  const [reminders, setReminders] = useState(() => {
    try {
      const stored = localStorage.getItem("spendora_reminders");
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });
  const [showMissedModal, setShowMissedModal] = useState(false);
  const [missedReminders, setMissedReminders] = useState([]);
  const notificationTimer = useRef();

  const navigate = useNavigate();
  const { lang, setLang } = useLang();
  const t = locales[lang];

  // ---- Reminder Functions ----
  // เวลาที่ควรแจ้งเตือนจริง (type safe)
  function getReminderDueDate(r) {
    if (r.repeatType === "everyday" || r.repeatType === "specificDays") {
      // เวลาเก็บ "08:00" → เทียบวันนี้
      const today = new Date();
      const [hh, mm] = r.time.split(":");
      today.setHours(Number(hh), Number(mm), 0, 0);
      return today;
    }
    // specificDate, dateRange = เป็น iso string เต็ม
    return new Date(r.time);
  }

  // Save reminders to localStorage
  useEffect(() => {
    localStorage.setItem("spendora_reminders", JSON.stringify(reminders));
  }, [reminders]);

  // Missed reminders
  const checkMissedReminders = useCallback(() => {
    const now = new Date();
    setMissedReminders(reminders.filter(
      r => !r.notified && getReminderDueDate(r) < now
    ));
  }, [reminders]);

  useEffect(() => {
    checkMissedReminders();
  }, [reminders, checkMissedReminders]);

  // Play sound + toast
  const showReminderToast = useCallback((reminder) => {
    toast(
      <span>
        <b>🔔 {reminder.message}</b>
        <br />
        <small>{formatDateTime(reminder.time, lang)}</small>
      </span>,
      { duration: 9000 }
    );
    if (reminder.sound && reminder.sound !== "Silent") {
      try {
        const file = reminder.sound === "Chime"
          ? "/sounds/chime.wav"
          : reminder.sound === "Bell"
            ? "/sounds/bell.wav"
            : null;
        if (file) {
          const audio = new Audio(file);
          audio.play();
        }
      } catch {}
    }
  }, [lang]);

  // Notification Loop (30s)
  useEffect(() => {
    function handleLoop() {
      const now = new Date();
      reminders.forEach((r, idx) => {
        const due = getReminderDueDate(r);
        if (!r.notified && due <= now) {
          showReminderToast(r);
          setReminders(prev => {
            const updated = prev.map((rem, i) =>
              i === idx ? { ...rem, notified: true } : rem
            );
            const nextTime = getNextReminderTime(r);
            if (nextTime) {
              updated.push({ ...r, time: nextTime, notified: false });
            }
            return updated;
          });
        }
      });
      checkMissedReminders();
    }
    notificationTimer.current = setInterval(handleLoop, 30000);
    return () => clearInterval(notificationTimer.current);
  }, [reminders, checkMissedReminders, showReminderToast]);

  

  // ลบ, บันทึก, dismiss
  function handleDeleteReminder(idx) {
    setReminders(prev => prev.filter((_, i) => i !== idx));
    toast.success(lang === "th" ? "ลบการแจ้งเตือนแล้ว" : "Reminder deleted!");
  }
  function handleSaveReminder(data) {
    setReminders(prev => [...prev, { ...data, notified: false }]);
    setShowReminderForm(false);
    toast.success(lang === "th" ? "บันทึกการแจ้งเตือนแล้ว" : "Reminder saved!");
  }
  function handleDismissMissedReminder(idx) {
    const dismissId = missedReminders[idx]?.time;
    setReminders(prev =>
      prev.map(r =>
        r.time === dismissId ? { ...r, notified: true } : r
      )
    );
    setMissedReminders(prev => prev.filter((_, i) => i !== idx));
  }

  // ---- Non-reminder (CSV, export, admin, plan, etc) ----
  useEffect(() => {
    if (!userProfile?.plan) return;
    api.planCatalog.list()
      .then(plans => {
        const plan = (plans || []).find(p => p.plan_id === userProfile.plan);
        setFeatures(plan?.features || {});
      })
      .catch(() => {});
  }, [userProfile?.plan]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    api.activities.list()
      .then(data => setActivities(data || []))
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function exportCSV(filtered) {
    setShowExportModal(false);
    if (!filtered || filtered.length === 0) {
      toast.error(lang === "th" ? "ไม่มีข้อมูลสำหรับ export" : "No data to export");
      return;
    }
  
    const csvHeader = lang === "th"
      ? "วันที่,กิจกรรม,หมวด,หมายเหตุ,ระยะเวลา\n"
      : "Date,Activity,Category,Note,Duration\n";
  
    const csvRows = filtered.map(a =>
      [
        a.activity_date,
        a.activity,
        a.category,
        a.note || "",
        a.duration || ""
      ].map(v => `"${(v || "").replace(/"/g, '""')}"`).join(",")
    );
  
    // ใส่ BOM สำหรับ Excel
    const BOM = "\uFEFF";
    const csvContent = BOM + csvHeader + csvRows.join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute("download", "spendora-activities.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  
    toast.success(lang === "th" ? "ส่งออกไฟล์เรียบร้อย!" : "Exported CSV!");
  }
  

  useEffect(() => {
    localStorage.setItem("chartView", chartView);
  }, [chartView]);

  useEffect(() => {
    const email = user?.emailAddresses?.[0]?.emailAddress || user?.email || "";
    setUserRole(adminEmails.includes(email) ? "admin" : "user");
  }, [user]);

  // Logout
  const handleLogout = async () => {
    await signOut();
    navigate("/");
  };

  // Share
  const handleShare = async () => {
    const shareData = {
      title: "Spendora – Time & Activity Tracker",
      text: "Track your time and habits with Spendora! Minimal, fun, and insightful.",
      url: APP_URL,
    };
    if (navigator.share) {
      try {
        await navigator.share(shareData);
        toast.success("Shared successfully!");
      } catch {}
    } else if (navigator.clipboard) {
      try {
        await navigator.clipboard.writeText(APP_URL);
        toast.success("App link copied to clipboard!");
      } catch {
        alert("Copy failed, please copy this link manually: " + APP_URL);
      }
    } else {
      alert("Please copy this link and share: " + APP_URL);
    }
  };

  // Check update
  const handleCheckUpdate = () => {
    setCheckingUpdate(true);
    setShowUpdated(false);
    setTimeout(() => {
      setCheckingUpdate(false);
      setShowUpdated(true);
      setTimeout(() => setShowUpdated(false), 1800);
    }, 1600);
  };

  // === RENDER ===
  return (
    <div className="max-w-md mx-auto p-4 mt-0 space-y-2">
      {/* Missed Reminder Modal */}
      <MissedReminderModal
        reminders={showMissedModal ? missedReminders : []}
        lang={lang}
        onDismiss={handleDismissMissedReminder}
        onClose={() => setShowMissedModal(false)}
      />

      {/* Missed Banner */}
      {missedReminders.length > 0 && !showMissedModal && (
        <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-800 p-3 mb-3 rounded flex items-center gap-2">
          <span>
            ⏰ {lang === "th"
              ? `คุณมี Reminder ที่เลยเวลาแล้ว ${missedReminders.length} รายการ`
              : `${missedReminders.length} reminders overdue!`}
          </span>
          <button
            className="ml-auto px-3 py-1 bg-yellow-300 hover:bg-yellow-400 rounded text-xs"
            onClick={() => setShowMissedModal(true)}
          >
            {lang === "th" ? "ดูรายละเอียด" : "View"}
          </button>
        </div>
      )}

      {/* Subscription Card */}
      {!userProfile?.is_premium ? (
        <div className="bg-gradient-to-r from-pink-300 to-blue-200 rounded-xl shadow p-4 mb-4 flex flex-col gap-2">
          <div className="text-lg font-bold">💎 {t.sub}</div>
          <div className="text-sm text-gray-700">{t.subDesc}</div>
          <button
            className="mt-2 bg-blue-500 text-white rounded px-4 py-2 font-semibold shadow hover:scale-105 transition"
            onClick={() => navigate("/subscribe")}
          >
            {t.sub}
          </button>
        </div>
      ) : (
        <div className="bg-gradient-to-r from-yellow-100 to-blue-50 rounded-xl shadow p-4 mb-4 flex flex-col gap-2">
          <div className="text-lg font-bold">💎 {lang === "th" ? "ขอบคุณที่สนับสนุน Spendora!" : "Thank you for supporting Spendora!"}</div>
          <div className="text-green-600 font-semibold mb-2">
            {lang === "th" ? "สถานะ: Premium ✓" : "Status: Premium ✓"}
          </div>
          {/*<button
            className="bg-blue-500 text-white rounded px-4 py-2 font-semibold shadow hover:scale-105 transition"
            onClick={() => navigate("/subscribe/manage")}
          >
            {lang === "th" ? "จัดการสมาชิกภาพ" : "Manage Subscription"}
          </button> */}
          <div className="text-center mt-2">
            <button
              className="mt-2 bg-yellow-400 text-white rounded px-4 py-2 font-semibold shadow hover:scale-105 transition w-fit"
              onClick={() => window.open("https://buymeacoffee.com/spendora?status=1", "_blank")}
            >
              {lang === "th" ? "สนับสนุนเพิ่มเติม" : "Keep Spendora Caffeinated!"}
            </button>
          </div>
        </div>
      )}

      {/* Special Setting Card */}
      <SpecialSettingCard user={user} userProfile={userProfile} />

      {/* Settings */}
      <div className="bg-white rounded-2xl shadow-sm p-5 space-y-5 border border-gray-100">
        <div className="font-semibold text-gray-800 text-base mb-2">{t.settings}</div>
        {/* Language */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Languages className="text-blue-500" size={22} />
            <span className="text-sm text-gray-700">{t.language}</span>
          </div>
          <select
            value={lang}
            onChange={e => setLang(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-1 text-sm focus:outline-none focus:ring focus:ring-blue-200"
          >
            <option value="en">English</option>
            <option value="th">ภาษาไทย</option>
          </select>
        </div>
        {/* Chart View */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ChartSpline className="text-green-500" size={22} />
            <span className="text-sm text-gray-700">{t.chartView}</span>
          </div>
          <select
            value={chartView}
            onChange={e => setChartView(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-1 text-sm focus:outline-none focus:ring focus:ring-blue-200"
          >
            <option value="weekly">{lang === "th" ? "รายสัปดาห์" : "Weekly"}</option>
            <option value="monthly">{lang === "th" ? "รายเดือน" : "Monthly"}</option>
          </select>
        </div>
        {/* Reminder Button + List */}
        <div>
          <button className="w-full flex items-center gap-2 bg-yellow-50 border border-yellow-200 rounded-xl px-3 py-2 font-semibold shadow hover:bg-yellow-100 transition"
            onClick={() => setShowReminderForm(true)}>
            <svg width="18" height="18" fill="none"><circle cx="9" cy="9" r="8" stroke="#FFC700" strokeWidth="2" /><path d="M9 5v3" stroke="#FFC700" strokeWidth="2" strokeLinecap="round"/><circle cx="9" cy="12.5" r="1" fill="#FFC700"/></svg>
            {lang === "th" ? "ตั้งค่าการแจ้งเตือน" : "Set up Reminders"}
          </button>
          {reminders.length > 0 && (
            <ul className="mt-2 text-xs text-gray-600 space-y-1">
              {reminders.map((r, idx) => (
                <li key={idx} className="flex items-center justify-between">
                  <span>
                    <span className="mr-2">🔔</span>
                    {r.message} <span className="text-gray-400">
                      ({formatDateTime(
                        r.repeatType === "everyday" || r.repeatType === "specificDays"
                          ? r.time
                          : r.time, lang
                      )})
                    </span>
                    {r.notified && (
                      <span className="ml-1 text-green-400" title="Notified">✔️</span>
                    )}
                  </span>
                  <button className="ml-2 text-red-400 hover:text-red-600"
                    aria-label="Delete" onClick={() => handleDeleteReminder(idx)}>
                    <svg width="16" height="16" fill="none" viewBox="0 0 24 24"><path d="M6 19a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V7H6zm3-9v6m4-6v6m1-8V4a2 2 0 0 0-2-2h-2a2 2 0 0 0-2 2v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        {/* Motivation Button */}
        <div className="mt-3">
          <button
            className="w-full flex items-center gap-2 bg-indigo-50 border border-indigo-200 rounded-xl px-3 py-2 font-semibold shadow hover:bg-indigo-100 transition"
            onClick={handleShowMotivation}
          >
            <svg width="20" height="20" fill="none" stroke="#6366F1" strokeWidth="2" viewBox="0 0 24 24">
              <path d="M12 20l9-5-9-5-9 5 9 5z" />
              <path d="M12 12l9-5-9-5-9 5 9 5z" />
            </svg>
            {lang === "th" ? "กระตุกจิตใจกันหน่อย 💡" : "Daily Spark 💡"}
          </button>
        </div>
          {motivationQuote && (
            <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center">
              <div className="bg-white rounded-2xl shadow-xl p-6 w-[90%] max-w-md text-center space-y-4">
                <div className="text-4xl">💡</div>
                <div className="text-lg font-semibold text-gray-800 leading-snug">{motivationQuote}</div>
                <button
                  onClick={() => setMotivationQuote(null)}
                  className="mt-3 px-4 py-2 bg-indigo-500 text-white rounded-lg font-semibold hover:bg-indigo-600 transition"
                >
                  {lang === "th" ? "ปิดข้อความ" : "Close"}
                </button>
              </div>
            </div>
          )}
        </div>

      {/* PREMIUM FEATURES CARD */}
      <div className="bg-gradient-to-tr from-yellow-50 via-blue-50 to-pink-50 border border-yellow-200 rounded-2xl p-4 mb-4 shadow-sm">
        <div className="flex items-center gap-2 mb-4">
          <Star className="text-yellow-400 fill-yellow-300" size={24} />
          <span className="font-semibold text-lg tracking-tight">
            {lang === "th" ? "Spendora Premium" : "Spendora Premium"}
          </span>
          {userProfile?.is_premium && (
            <svg className="ml-1 text-green-600 h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path d="M9 12l2 2 4-4"/>
            </svg>
          )}
        </div>
        <ul className="space-y-3 mb-2">
          {/* Export as CSV */}
          <li className="flex items-center gap-3">
            <Download className="text-green-600" size={20} />
            <div className="flex-1 text-gray-900 text-base">
              {lang === "th" ? "ส่งออกกิจกรรมเป็นไฟล์ CSV" : "Export activities as CSV"}
            </div>
            {features.export_csv && userProfile?.is_premium ? (
              <button
                className="px-3 py-1 bg-green-500 hover:bg-green-600 text-white text-xs rounded-lg font-semibold"
                onClick={() => setShowExportModal(true)}
              >
                {lang === "th" ? "ใช้งาน" : "Use"}
              </button>
            ) : (
              <span className="px-3 py-1 text-gray-400 bg-gray-100 rounded text-xs font-medium">
                {lang === "th" ? "Premium" : "Premium"}
              </span>
            )}
          </li>
          {/* View Insights */}
          <li className="flex items-center gap-3">
            <ChartSpline className="text-blue-500" size={20} />
            <div className="flex-1 text-gray-900 text-base">
              {lang === "th" ? "ดูสถิติและอินไซต์กิจกรรมขั้นสูง" : "View advanced Insights & analytics"}
            </div>
            {userProfile?.is_premium ? (
              <button
                className="px-3 py-1 bg-blue-500 hover:bg-blue-600 text-white text-xs rounded-lg font-semibold"
                onClick={() => navigate("/infographic?tab=insights")}
              >
                {lang === "th" ? "ดูเลย" : "View"}
              </button>
            ) : (
              <span className="px-3 py-1 text-gray-400 bg-gray-100 rounded text-xs font-medium">
                {lang === "th" ? "Premium" : "Premium"}
              </span>
            )}
          </li>
          {/* View Unlimit Activities */}
          <li className="flex items-center gap-3">
            <Infinity className="text-blue-500" size={20} />
            <div className="flex-1 text-gray-900 text-base">
              {lang === "th" ? "บันทึกกิจกรรมได้ไม่จำกัด" : "Unlimited activity"}
            </div>
            <span className="px-3 py-1 text-gray-400 bg-gray-100 rounded text-xs font-medium">
              {lang === "th" ? "Premium" : "Premium"}
            </span>
          </li>
        </ul>
        {/* Call to Action for Freemium */}
        {!userProfile?.is_premium && (
          <div className="mt-2 text-center">
            <button
              onClick={() => navigate("/subscribe")}
              className="inline-flex items-center px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white font-semibold rounded-xl shadow transition"
            >
              {lang === "th"
                ? "อัปเกรดเป็น Premium เพื่อปลดล็อกฟีเจอร์เหล่านี้"
                : "Upgrade to Premium to unlock these features"}
            </button>
          </div>
        )}
      </div>
      

      {/* Others */}
      <div className="bg-white rounded-xl shadow p-4 space-y-2">
        <div className="font-bold text-gray-800 mb-1">{t.others}</div>

        {/* Check Update Version Button */}
        <button
          className={`w-full text-left py-2 font-semibold flex items-center gap-2 relative ${
            checkingUpdate ? "opacity-60 pointer-events-none" : "hover:underline text-blue-700"
          }`}
          onClick={handleCheckUpdate}
          disabled={checkingUpdate}
        >
          <motion.span
            animate={checkingUpdate ? { rotate: 360 } : { rotate: 0 }}
            transition={{ repeat: checkingUpdate ? Infinity : 0, duration: 0.7, ease: "linear" }}
            className="inline-block"
          >
            <svg className="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" strokeWidth={2}
              viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round"
                d="M21 2v6h-6M3 22v-6h6M3.51 9a9 9 0 0114.13-3.36M20.49 15A9 9 0 015.5 18.36" />
            </svg>
          </motion.span>
          {lang === "th"
            ? "เช็คอัปเดตเวอร์ชัน"
            : "Check Update Version"}
        </button>
        {/* Bar loading effect */}
        <AnimatePresence>
          {checkingUpdate && (
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: "100%" }}
              exit={{ width: 0 }}
              transition={{ duration: 1.2, ease: "easeInOut" }}
              className="h-1 bg-gradient-to-r from-blue-400 to-pink-400 rounded-full mt-2 mb-1"
            />
          )}
        </AnimatePresence>
        {/* Toast อัปเดตเสร็จ */}
        <AnimatePresence>
          {showUpdated && (
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              transition={{ duration: 0.38 }}
              className="w-full rounded-lg bg-indigo-50 p-3 text-center text-indigo-700 font-semibold shadow mt-2"
            >
              {lang === "th"
                ? "Spendora ของท่านได้อัปเดตเป็นเวอร์ชันล่าสุดแล้ว 🎉"
                : "Your Spendora is up to date! 🎉"}
            </motion.div>
          )}
        </AnimatePresence>

        <button
          className="w-full text-left py-2 hover:underline"
          onClick={handleShare}
        >
          {t.share}
        </button>
        <button
          className="w-full text-left py-2 hover:underline"
          onClick={() => setShowFeedback(true)}
        >
          {lang === "th" ? "ข้อเสนอแนะ / แจ้งปัญหา" : "Feedback / Bug Report"}
        </button>
        {userRole === "admin" && (
          <button
            className="w-full text-left py-2 hover:underline text-indigo-700 font-semibold"
            onClick={() => setShowAdmin(true)}
          >
            {t.admin}
          </button>
        )}
        <button
          className="w-full text-left py-2 hover:underline"
          onClick={() => setShowAbout(true)}
        >
          {t.about}
        </button>
        <button
          className="w-full text-left py-2 hover:underline"
          onClick={() => navigate("/welcome")}
        >
          🌟 {lang === "th" ? "อยากรู้จัก Spendora มากขึ้น?" : "Spendora Website"}
        </button>
      </div>

      {/* Divider + Logout */}
      <div className="pt-5 mt-3 border-t border-gray-200">
        <button
          onClick={handleLogout}
          className="w-full flex justify-center items-center gap-2 bg-red-50 text-red-700 font-semibold rounded-xl py-3 mt-2 shadow-sm hover:bg-red-100 active:scale-95 transition text-base"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1m0-10v1" />
          </svg>
          {t.logout}
        </button>
      </div>

      {/* Version */}
      <div className="text-center text-xs text-gray-400 pt-4 select-none">
        Spendora v{version}
      </div>

      {/* Modals */}
      <AboutModal show={showAbout} onClose={() => setShowAbout(false)} />
      <FeedbackModal show={showFeedback} onClose={() => setShowFeedback(false)} />
      <AdminDashboardModal show={showAdmin} onClose={() => setShowAdmin(false)} />
      {/* Export CSV Modal */}
      <CSVModal
        open={showExportModal}
        onClose={() => setShowExportModal(false)}
        onExport={exportCSV}
        lang={lang}
        activities={activities}
      />

      {/* ---- Reminder Modal ---- */}
      {showReminderForm && (
        <div className="fixed z-50 inset-0 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/30" onClick={() => setShowReminderForm(false)} />
          <div className="relative bg-white rounded-2xl shadow-lg p-4 w-[400px] max-w-full mx-auto">
            <CustomReminderForm
              lang={lang}
              onSave={handleSaveReminder}
              onCancel={() => setShowReminderForm(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
}